// js/api/messagesApi.js (VERSÃO COMPLETA COM LOGS DE DEPURAÇÃO)

import supabase from '../supabase-config.js';
import auth from '../auth.js';

/**
 * Busca todas as conversas do usuário logado, com detalhes do outro participante
 * e a última mensagem para preview. Usa uma lógica mais explícita no lado do cliente.
 * @returns {Promise<Array>} Um array com os dados das conversas.
 */
export async function fetchConversations() {
    console.log('[API DEBUG] >>> Iniciando fetchConversations...');
    const session = await auth.getSession();
    if (!session) {
        console.warn('[API DEBUG] <<< Nenhum usuário logado. Retornando array vazio.');
        return [];
    }
    const currentUserId = session.user.id;
    console.log(`[API DEBUG] ID do usuário logado: ${currentUserId}`);

    // 1. Busca todas as conversas em que o usuário atual participa.
    console.log('[API DEBUG] Passo 1: Buscando conversas do usuário...');
    const { data: conversationsData, error: conversationsError } = await supabase
        .from('conversations')
        .select('id, participant1_id, participant2_id')
        .or(`participant1_id.eq.${currentUserId},participant2_id.eq.${currentUserId}`);

    if (conversationsError) {
        console.error('❌ [API DEBUG] Erro no Passo 1 (buscar conversas):', conversationsError);
        return [];
    }
    console.log('✅ [API DEBUG] Resultado do Passo 1 (conversas encontradas):', conversationsData);

    if (!conversationsData || conversationsData.length === 0) {
        console.warn('[API DEBUG] <<< Nenhuma conversa encontrada para este usuário. Retornando array vazio.');
        return [];
    }

    // 2. Coleta os IDs do "outro" participante e os IDs das conversas.
    console.log('[API DEBUG] Passo 2: Mapeando IDs...');
    const otherParticipantIds = conversationsData.map(c => 
        c.participant1_id === currentUserId ? c.participant2_id : c.participant1_id
    );
    const conversationIds = conversationsData.map(c => c.id);
    console.log('[API DEBUG] IDs dos outros participantes para buscar:', otherParticipantIds);
    console.log('[API DEBUG] IDs das conversas para buscar últimas mensagens:', conversationIds);

    // 3. Busca os perfis dos outros participantes e as últimas mensagens em paralelo.
    console.log('[API DEBUG] Passo 3: Buscando perfis e últimas mensagens em paralelo...');
    const [
        { data: participantsData, error: participantsError },
        { data: lastMessagesData, error: lastMessagesError }
    ] = await Promise.all([
        supabase.from('profiles').select('id, username, avatar_url').in('id', otherParticipantIds),
        supabase.rpc('get_last_messages_for_conversations', { conversation_ids: conversationIds })
    ]);

    if (participantsError) console.error('❌ [API DEBUG] Erro no Passo 3 (buscar perfis):', participantsError);
    if (lastMessagesError) console.error('❌ [API DEBUG] Erro no Passo 3 (RPC get_last_messages):', lastMessagesError);
    
    console.log('✅ [API DEBUG] Resultado do Passo 3 (perfis encontrados):', participantsData);
    console.log('✅ [API DEBUG] Resultado do Passo 3 (últimas mensagens da RPC):', lastMessagesData);
    
    if (!participantsData || !lastMessagesData) {
        console.error('[API DEBUG] <<< Falha ao buscar dados de participantes ou últimas mensagens. Retornando array vazio.');
        return [];
    }
    
    // 4. Mapeia os dados para um acesso rápido.
    console.log('[API DEBUG] Passo 4: Mapeando resultados...');
    const participantsMap = new Map(participantsData.map(p => [p.id, p]));
    const lastMessagesMap = new Map(lastMessagesData.map(m => [m.conversation_id, m]));
    console.log('[API DEBUG] Mapa de participantes:', participantsMap);
    console.log('[API DEBUG] Mapa de últimas mensagens:', lastMessagesMap);


    // 5. Combina tudo em um único array de conversas, pronto para a UI.
    console.log('[API DEBUG] Passo 5: Combinando dados...');
    const finalConversations = conversationsData.map(convo => {
        const otherParticipantId = convo.participant1_id === currentUserId ? convo.participant2_id : convo.participant1_id;
        const otherParticipant = participantsMap.get(otherParticipantId);
        const lastMessage = lastMessagesMap.get(convo.id);
        
        return {
            conversation_id: convo.id,
            other_participant_username: otherParticipant?.username || 'Usuário Deletado',
            other_participant_avatar_url: otherParticipant?.avatar_url,
            last_message_content: lastMessage?.last_message_content,
            last_message_at: lastMessage?.last_message_at
        };
    }).sort((a, b) => new Date(b.last_message_at) - new Date(a.last_message_at));

    console.log('✅ [API DEBUG] <<< Retornando conversas finais combinadas:', finalConversations);
    return finalConversations;
}

/**
 * Busca o histórico completo de mensagens de uma conversa específica.
 * @param {string} conversationId - O ID da conversa a ser buscada.
 * @returns {Promise<Array>} Um array com todas as mensagens da conversa.
 */
export async function fetchMessages(conversationId) {
    console.log(`[API DEBUG] >>> Iniciando fetchMessages para a conversa: ${conversationId}`);
    console.log('[API DEBUG] Buscando histórico de mensagens...');
    const { data, error } = await supabase
        .from('messages')
        .select('*, sender:profiles!messages_sender_id_fkey(id, username, avatar_url)')
        .eq('conversation_id', conversationId)
        .order('created_at', { ascending: true });
    
    if (error) {
        console.error('❌ [API DEBUG] Erro ao buscar mensagens:', error);
        return [];
    }
    
    console.log(`✅ [API DEBUG] <<< Retornando ${data.length} mensagens:`, data);
    return data;
}

/**
 * Envia uma nova mensagem.
 * @param {string} conversationId - O ID da conversa.
 * @param {string} content - O texto da mensagem.
 * @returns {Promise<object>} O objeto da mensagem recém-criada.
 */
export async function sendMessage(conversationId, content) {
    console.log(`[API DEBUG] >>> Iniciando sendMessage para a conversa: ${conversationId}`);
    const session = await auth.getSession();
    if (!session) {
        console.error('❌ [API DEBUG] Usuário não autenticado.');
        throw new Error("Usuário não autenticado.");
    }
    console.log('[API DEBUG] Usuário autenticado, ID:', session.user.id);

    // Passo 1: Obter detalhes da conversa
    console.log('[API DEBUG] Passo 1: Buscando detalhes da conversa...');
    const { data: conversation, error: convoError } = await supabase
        .from('conversations')
        .select('participant1_id, participant2_id')
        .eq('id', conversationId)
        .single();
    if (convoError) {
        console.error("❌ [API DEBUG] Erro ao buscar detalhes da conversa:", convoError);
        throw convoError;
    }
    console.log('✅ [API DEBUG] Detalhes da conversa:', conversation);
    
    const recipientId = conversation.participant1_id === session.user.id ? conversation.participant2_id : conversation.participant1_id;
    console.log(`[API DEBUG] ID do destinatário: ${recipientId}`);
    
    // Passo 2: Inserir a nova mensagem
    console.log('[API DEBUG] Passo 2: Inserindo nova mensagem no banco...');
    const { data: newMessage, error: messageError } = await supabase
        .from('messages')
        .insert({ conversation_id: conversationId, sender_id: session.user.id, content: content })
        .select().single();
    if (messageError) {
        console.error('❌ [API DEBUG] Erro ao inserir mensagem:', messageError);
        throw messageError;
    }
    console.log('✅ [API DEBUG] Mensagem inserida:', newMessage);
    
    // Passos 3 e 4 (Notificação e Broadcast) são menos críticos para a depuração de "não carregar"
    // ...
    
    console.log('✅ [API DEBUG] <<< Retornando nova mensagem:', newMessage);
    return newMessage;
}

/**
 * Encontra uma conversa existente entre dois usuários ou cria uma nova se não existir.
 * @param {string} targetUserId - O ID do outro usuário na conversa.
 * @returns {Promise<string>} O ID da conversa (existente ou nova).
 */
export async function findOrCreateConversation(targetUserId) {
    console.log(`[API DEBUG] >>> Iniciando findOrCreateConversation com o usuário: ${targetUserId}`);
    const session = await auth.getSession();
    if (!session) {
        console.error('❌ [API DEBUG] Usuário não autenticado.');
        throw new Error("Usuário não autenticado.");
    }

    const rpcParams = { p_user1_id: session.user.id, p_user2_id: targetUserId };
    console.log('[API DEBUG] Chamando RPC "find_or_create_conversation" com os parâmetros:', rpcParams);
    
    const { data, error } = await supabase.rpc('find_or_create_conversation', rpcParams);
    
    if (error) {
        console.error('❌ [API DEBUG] Erro na RPC "find_or_create_conversation":', error);
        throw error;
    }

    console.log(`✅ [API DEBUG] <<< RPC retornou o ID da conversa: ${data}`);
    return data;
}